import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addAppToHome,
  type AppSummary,
  listApps,
  rollbackApp,
  shareApp,
  unpublishApp,
} from "../../api/hub/apps";
import { getMe } from "../../api/hub/identity";
import { AdminAppsOverview } from "./AdminAppsOverview";
import { AppMetadata, AppScope, statusLabel } from "./AppMetadata";
import { SandboxApp } from "./SandboxApp";
import { UploadPanel } from "./UploadPanel";
import { VersionPanel } from "./VersionPanel";
import "./apps.css";
export function AppsPage() {
  const query = useQuery({
      queryKey: ["hub", "apps", "list"],
      queryFn: listApps,
      refetchInterval: 3000,
    }),
    me = useQuery({ queryKey: ["hub", "me"], queryFn: getMe }),
    qc = useQueryClient();
  const [tab, setTab] = useState<"mine" | "shared">("mine"),
    [search, setSearch] = useState(""),
    [editor, setEditor] = useState<AppSummary | true | null>(null),
    [opened, setOpened] = useState<AppSummary | null>(null),
    [sharedView, setSharedView] = useState(false),
    [selected, setSelected] = useState<AppSummary | null>(null),
    [admin, setAdmin] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [retryAction, setRetryAction] = useState<(() => Promise<unknown>) | null>(
      null,
    );
  const refresh = () => qc.invalidateQueries({ queryKey: ["hub", "apps"] });
  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setError("");
    setRetryAction(null);
    try {
      const result = await action();
      if (
        result && typeof result === "object" && "id" in result &&
        selected?.id === (result as AppSummary).id
      ) setSelected(result as AppSummary);
      setMessage(success);
      await refresh();
      await qc.invalidateQueries({ queryKey: ["hub", "home"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败");
      setRetryAction(() => action);
    } finally {
      setBusy(false);
    }
  };
  const canManage = (app: AppSummary) =>
    !!me.data && !me.data.isAI &&
    (app.ownerId === me.data.id || ["CEO", "CTO"].includes(me.data.role));
  const entries = (query.data?.[tab] ?? []).filter((a) =>
    `${a.name} ${a.description} ${a.ownerName}`.toLowerCase().includes(
      search.toLowerCase(),
    )
  );
  const selectedLive = selected &&
    (query.data?.mine.find((a) => a.id === selected.id) ??
      query.data?.shared.find((a) => a.id === selected.id) ?? selected);
  return (
    <section className="apps-page">
      <header className="apps-heading">
        <div>
          <h1>应用中心</h1>
          <p>打开常用应用，或加到我的主页。</p>
        </div>
        <div className="apps-actions">
          <button
            className="apps-primary"
            onClick={() => {
              setEditor(true);
              setError("");
            }}
          >
            上传应用
          </button>
          {me.data && !me.data.isAI && ["CEO", "CTO"].includes(me.data.role) &&
            (
              <button onClick={() => setAdmin(!admin)}>
                {admin ? "收起管理总览" : "管理总览"}
              </button>
            )}
        </div>
      </header>
      {editor && (
        <UploadPanel
          key={editor === true ? "new" : editor.id}
          app={editor === true ? undefined : editor}
          onClose={() => setEditor(null)}
          onSaved={(app) => {
            setEditor(null);
            setTab("mine");
            setMessage(
              `「${app.name}」第 ${
                app.privateCurrentVersion ?? app.version
              } 版已发布为仅自己可用`,
            );
            void refresh();
          }}
        />
      )}
      <nav className="apps-toolbar" aria-label="作品范围">
        <div role="tablist" aria-label="应用范围">
          <button
            role="tab"
            aria-selected={tab === "mine"}
            onClick={() => setTab("mine")}
          >
            我的应用 <span>{query.data?.mine.length ?? 0}</span>
          </button>
          <button
            role="tab"
            aria-selected={tab === "shared"}
            onClick={() => setTab("shared")}
          >
            全员作品 <span>{query.data?.shared.length ?? 0}</span>
          </button>
        </div>
        <label>
          <span className="apps-visually-hidden">搜索应用</span>
          <input
            type="search"
            placeholder="搜索名称、用途或作者"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <button
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          刷新
        </button>
      </nav>
      {(error || query.error) && (
        <div className="apps-error" role="alert">
          <p>{error || query.error?.message}。请重试。</p>
          <button
            disabled={busy}
            onClick={() =>
              retryAction
                ? void run(retryAction, "操作已完成")
                : void query.refetch()}
          >
            重试操作
          </button>
        </div>
      )}
      {message && <p role="status" className="apps-message">{message}</p>}
      {query.isPending
        ? <p role="status">正在读取应用作品库…</p>
        : (
          <div className="apps-library" role="tabpanel">
            <div className="apps-column-labels">
              <span>作品与作者</span>
              <span>使用范围</span>
              <span>操作</span>
            </div>
            {entries.map((app) => (
              <article className="apps-work-row" key={app.id}>
                <div className="apps-work-name">
                  <span className="apps-icon" aria-hidden="true">
                    {app.icon ?? "◈"}
                  </span>
                  <div>
                    <h2>{app.name}</h2>
                    <p>{app.description || "作者尚未填写用途"}</p>
                    <small>由 {app.ownerName} 发布</small>
                  </div>
                </div>
                <div className="apps-row-metadata">
                  <span className="apps-status">{statusLabel(app)}</span>
                  {app.approval?.status === "pending" && (
                    <span className="apps-pending">新版待审批</span>
                  )}
                </div>
                <div className="apps-row-actions">
                  <button
                    className="apps-primary"
                    disabled={app.status === "disabled"}
                    onClick={() => {
                      setSharedView(tab === "shared");
                      setOpened(app);
                    }}
                  >
                    打开应用
                  </button>
                  <button
                    disabled={busy || app.status === "disabled"}
                    onClick={() =>
                      void run(() =>
                        addAppToHome(app.id), "已加到个人主页")}
                  >
                    加到个人主页
                  </button>
                  <details className="apps-manage-details">
                    <summary>权限与管理</summary>
                    <AppMetadata app={app} />
                    <AppScope app={app} />
                    {canManage(app) && (
                      <>
                        <button onClick={() => setSelected(app)}>
                          查看版本
                        </button>
                        {app.ownerId === me.data?.id && (
                          <>
                            <button onClick={() => setEditor(app)}>
                              发布新版本
                            </button>
                            <button
                              disabled={busy || app.status === "disabled" ||
                                app.approval?.status === "pending"}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `申请将「${app.name}」作者当前第 ${
                                      app.privateCurrentVersion ?? app.version
                                    } 版共享给全员？\nL2 数据、外联或设备权限需 CEO 审批。`,
                                  )
                                ) {
                                  void run(
                                    () =>
                                      shareApp(app.id),
                                    "共享申请已提交，请查看真实审批状态",
                                  );
                                }
                              }}
                            >
                              申请全员共享
                            </button>
                          </>
                        )}
                        <button
                          disabled={busy || app.status === "disabled"}
                          onClick={() => {
                            if (
                              window.confirm(
                                `停用「${app.name}」？所有使用者的应用连接将关闭。`,
                              )
                            ) {
                              void run(
                                () => unpublishApp(app.id),
                                "应用已停用",
                              );
                            }
                          }}
                        >
                          停用
                        </button>
                      </>
                    )}
                  </details>
                </div>
              </article>
            ))}
            {!entries.length && (
              <div className="apps-empty">
                <h2>
                  {search
                    ? "没有匹配的作品"
                    : tab === "mine"
                    ? "你的第一个应用，可以很小。"
                    : "全员作品即将从这里开始。"}
                </h2>
                <p>
                  {tab === "mine"
                    ? "准备一份单文件 HTML 与 manifest，检查后发布，仅你自己可见。"
                    : "作者申请共享后，通过审批的精确版本会出现在这里。"}
                </p>
                {tab === "mine" && !search && (
                  <button onClick={() => setEditor(true)}>
                    上传第一个应用
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      {opened && (
        <div className="apps-viewer">
          <div className="apps-section-heading">
            <h2>{opened.name}</h2>
            <button onClick={() => setOpened(null)}>关闭应用</button>
          </div>
          <SandboxApp appId={opened.id} sharedOnly={sharedView} />
        </div>
      )}
      {selectedLive && (
        <VersionPanel
          app={selectedLive}
          onClose={() => setSelected(null)}
          busy={busy}
          onRollback={(version) => {
            if (
              window.confirm(
                `回滚「${selectedLive.name}」到第 ${version} 版，并恢复此版本的权限范围？`,
              )
            ) {
              void run(
                () => rollbackApp(selectedLive.id, version),
                "已恢复所选版本",
              );
            }
          }}
        />
      )}
      {admin && <AdminAppsOverview />}
    </section>
  );
}
